import React, { useEffect, useId, useRef, useState } from 'react';
import Check from 'lucide-react/dist/esm/icons/check.js';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2.js';
import X from 'lucide-react/dist/esm/icons/x.js';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle.js';

const formatTranslation = (template, values = {}) => Object.entries(values).reduce(
  (text, [key, value]) => text.replace(new RegExp(`{{${key}}}`, 'g'), value),
  template
);

const getPartnerInitials = (name = '') => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part.charAt(0).toUpperCase())
  .join('') || '?';

function PartnerAvatar({ partner, className = 'h-11 w-11' }) {
  if (partner?.logoUrl) {
    return (
      <img
        src={partner.logoUrl}
        alt=""
        className={`${className} shrink-0 rounded-lg border border-white/10 bg-white object-contain p-1`}
      />
    );
  }

  return (
    <span className={`${className} flex shrink-0 items-center justify-center rounded-lg border border-emerald-300/30 bg-emerald-400/15 text-xs font-bold text-emerald-100`}>
      {getPartnerInitials(partner?.name)}
    </span>
  );
}

export default function CheckoutPartnerSelection({
  partners = [],
  loading = false,
  selectedPartner,
  onPartnerChange,
  fieldLabel = '',
  required = false,
  variant = 'dark',
  t,
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listboxId = useId();
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listboxRef = useRef(null);
  const optionRefs = useRef([]);

  const closeListbox = () => setOpen(false);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) closeListbox();
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listboxRef.current?.focus();
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const selectedId = selectedPartner?.id || '';
  const selectedCode = selectedPartner?.code || '';
  const selected = partners.find((partner) => (
    (selectedId && partner.id && String(partner.id).toLowerCase() === String(selectedId).toLowerCase()) ||
    (selectedCode && partner.code && String(partner.code).toLowerCase() === String(selectedCode).toLowerCase())
  )) || null;

  useEffect(() => {
    const handleFocusRequired = () => {
      if (!required || selected) return;
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      buttonRef.current?.focus();
    };

    window.addEventListener('partner-required-focus', handleFocusRequired);
    return () => window.removeEventListener('partner-required-focus', handleFocusRequired);
  }, [required, selected]);

  const isLight = variant === 'light';

  if (loading) {
    return (
      <div className={`mb-4 flex items-center gap-2 rounded-2xl p-6 text-sm backdrop-blur ${isLight ? 'border border-gray-200 bg-white text-gray-600 shadow-sm' : 'border border-white/10 bg-white/5 text-white/60'}`}>
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('checkout.loadingPartners')}
      </div>
    );
  }

  if (partners.length === 0) return null;

  const selectedIndex = partners.findIndex((partner) => String(partner.id) === String(selected?.id || ''));
  const placeholderText = fieldLabel
    ? formatTranslation(t('checkout.choosePartnerToSupport'), { fieldName: fieldLabel.toLowerCase() })
    : t('checkout.choosePartner');
  const selectedSupportText = selected
    ? (fieldLabel
        ? formatTranslation(t('checkout.selectedPartnerSupportWithField'), { fieldName: fieldLabel, partnerName: selected.name })
        : formatTranslation(t('checkout.selectedPartnerSupport'), { partnerName: selected.name }))
    : placeholderText;
  const displayValue = selected ? selected.name : placeholderText;

  const openListbox = (index = selectedIndex >= 0 ? selectedIndex : 0) => {
    setActiveIndex(Math.max(0, index));
    setOpen(true);
  };

  const choosePartner = (partner) => {
    onPartnerChange?.(partner);
    closeListbox();
    buttonRef.current?.focus();
  };

  const clearPartner = () => {
    onPartnerChange?.(null);
    closeListbox();
    buttonRef.current?.focus();
  };

  const handleButtonKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openListbox(selectedIndex >= 0 ? selectedIndex : 0);
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      openListbox(selectedIndex >= 0 ? selectedIndex : partners.length - 1);
    }
  };

  const handleListboxKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeListbox();
      buttonRef.current?.focus();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % partners.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + partners.length) % partners.length);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(partners.length - 1);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choosePartner(partners[activeIndex]);
    }
  };

  const isMissingRequired = required && !selected;

  const containerClasses = isLight
    ? `relative mb-2 sm:mb-6 rounded-2xl border bg-white dark:bg-gray-800 p-3.5 sm:p-5 shadow-sm transition ${
        isMissingRequired ? 'border-red-300 dark:border-red-800/80' : 'border-gray-200 dark:border-gray-700'
      }`
    : `relative mb-2.5 sm:mb-4 rounded-2xl border bg-white/5 p-3.5 sm:p-6 backdrop-blur transition ${
        isMissingRequired ? 'border-red-500/40' : 'border-white/10'
      }`;

  const titleClasses = isLight ? 'text-base font-bold text-gray-900 dark:text-white' : 'text-lg font-semibold text-white mb-0.5 sm:mb-1';
  const subtitleClasses = isLight ? 'text-gray-500 dark:text-gray-400 text-xs mt-0.5' : 'text-white/50 text-xs mb-2.5 sm:mb-4';
  const buttonClasses = isLight
    ? `mt-3 flex w-full items-center gap-3 rounded-xl border p-3 text-left transition focus:outline-none focus:ring-2 ${
        isMissingRequired
          ? 'border-red-400 bg-red-50/40 text-red-900 focus:ring-red-400/60 dark:border-red-500/60 dark:bg-red-950/20'
          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 hover:border-emerald-300 dark:hover:border-emerald-500/50 hover:bg-emerald-50/30 focus:ring-emerald-400/60'
      }`
    : `flex w-full items-center gap-3 rounded-xl border p-3 text-left shadow-inner shadow-black/20 transition focus:outline-none focus:ring-2 ${
        isMissingRequired
          ? 'border-red-500/60 bg-red-950/30 hover:border-red-400/70 focus:ring-red-400/70 ring-1 ring-red-500/30'
          : 'border-white/15 bg-slate-950/40 hover:border-emerald-300/40 hover:bg-white/[0.07] focus:ring-emerald-400/70'
      }`;
  const labelClasses = isLight ? 'block truncate text-xs font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-400' : 'block truncate text-xs font-medium uppercase tracking-wide text-emerald-200/80';
  const valueClasses = isLight ? 'mt-0.5 block truncate text-sm font-semibold text-gray-900 dark:text-white' : 'mt-0.5 block truncate text-sm font-semibold text-white';
  const codeClasses = isLight ? 'mt-1 block truncate text-xs text-gray-500 dark:text-gray-400' : 'mt-1 block truncate text-xs text-white/45';
  const actionClasses = isLight ? 'flex shrink-0 items-center gap-1.5 sm:gap-2 text-xs font-semibold whitespace-nowrap text-gray-500 dark:text-gray-400' : 'flex shrink-0 items-center gap-1.5 sm:gap-2 text-xs font-semibold whitespace-nowrap text-white/55';
  const chevronActive = isLight ? 'rotate-180 text-emerald-600 dark:text-emerald-400' : 'rotate-180 text-emerald-200';
  const clearClasses = isLight
    ? 'mt-3 inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 transition hover:border-gray-300 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-400/60'
    : 'mt-3 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold text-white/65 transition hover:border-white/20 hover:bg-white/[0.06] hover:text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/60';
  const listboxClasses = isLight
    ? 'absolute left-3.5 sm:left-5 right-3.5 sm:right-5 top-full z-[70] mt-2 max-h-72 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/60'
    : 'absolute left-3.5 sm:left-4 right-3.5 sm:right-4 top-full z-[70] mt-2 max-h-72 overflow-y-auto rounded-xl border border-white/15 bg-slate-950/95 p-2 shadow-2xl shadow-black/50 backdrop-blur focus:outline-none focus:ring-2 focus:ring-emerald-400/60';
  const optionActive = isLight ? 'bg-emerald-50 dark:bg-emerald-950/40' : 'bg-emerald-400/15';
  const optionHover = isLight ? 'hover:bg-gray-50 dark:hover:bg-gray-700/60' : 'hover:bg-white/[0.06]';
  const optionSelected = isLight ? 'text-gray-900 dark:text-white' : 'text-white';
  const optionDefault = isLight ? 'text-gray-600 dark:text-gray-300' : 'text-white/75';
  const optionCode = isLight ? 'text-gray-400 dark:text-gray-500' : 'text-white/45';
  const checkColor = isLight ? 'text-emerald-600 dark:text-emerald-400' : 'text-emerald-300';

  return (
    <div ref={rootRef} className={`${containerClasses} ${open ? 'z-50' : 'z-20'}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className={titleClasses}>{fieldLabel || t('checkout.partnerSupport')}</h3>
          <p className={subtitleClasses}>
            {fieldLabel
              ? formatTranslation(t('checkout.choosePartnerToSupport'), { fieldName: fieldLabel.toLowerCase() })
              : t('checkout.partnerSupportSubtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {required && (
            <span className={isLight
              ? "inline-flex items-center rounded-md bg-red-50 dark:bg-red-950/40 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 ring-1 ring-inset ring-red-600/20"
              : "inline-flex items-center rounded-md bg-red-500/15 border border-red-500/30 px-2 py-0.5 text-xs font-semibold text-red-300 shadow-sm"
            }>
              {t('checkout.required')}
            </span>
          )}
          {selected && !required && (
            <button
              type="button"
              onClick={clearPartner}
              className={isLight ? 'text-xs text-gray-500 hover:text-red-600 flex items-center gap-1 transition-colors py-0.5' : 'text-xs text-red-300 hover:text-red-200 flex items-center gap-1 transition-colors py-0.5'}
              title={fieldLabel ? `${t('checkout.clear', 'Clear')} ${fieldLabel}` : t('checkout.clearPartner')}
            >
              <X className="h-3.5 w-3.5" />
              <span>{t('checkout.clear', 'Clear')}</span>
            </button>
          )}
        </div>
      </div>

      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={selected
          ? (fieldLabel ? `${t('checkout.changePartner', 'Change')} ${fieldLabel}: ${selected.name}` : `${t('checkout.changePartner', 'Change')}: ${selected.name}`)
          : (fieldLabel ? `${t('cinematic.choose', 'Choose')} ${fieldLabel}` : t('checkout.choosePartner'))}
        onClick={() => (open ? closeListbox() : openListbox())}
        onKeyDown={handleButtonKeyDown}
        className={buttonClasses}
      >
        <PartnerAvatar partner={selected || { name: fieldLabel || t('checkout.partnerSupport') }} />
        <span className="min-w-0 flex-1">
          <span className={labelClasses} title={selected ? (fieldLabel || t('checkout.selectedPartnerLabel')) : undefined}>
            {selected ? (fieldLabel || t('checkout.selectedPartnerLabel')) : (fieldLabel ? `${t('checkout.noSelection', 'No selection')}` : t('checkout.noPartnerSelected'))}
          </span>
          <span className={valueClasses} title={selected ? selectedSupportText : placeholderText}>
            {displayValue}
          </span>
          {selected?.code && (
            <span className={codeClasses}>
              {formatTranslation(t('checkout.partnerCode'), { code: selected.code })}
            </span>
          )}
        </span>
        <span className={actionClasses}>
          {selected ? t('checkout.changePartner', 'Change') : t('cinematic.choose', 'Choose')}
          <ChevronDown className={`h-4 w-4 transition ${open ? chevronActive : ''}`} />
        </span>
      </button>

      {isMissingRequired && (
        <p className="text-xs text-red-400 mt-2 flex items-center gap-1.5 font-medium">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-red-400" />
          <span>
            {fieldLabel
              ? formatTranslation(t('checkout.partnerRequiredWithField', 'Please choose a {{fieldName}} to proceed.'), { fieldName: fieldLabel.toLowerCase() })
              : t('checkout.partnerRequired', 'Please choose a partner to proceed.')}
          </span>
        </p>
      )}

      {open && (
        <div
          id={listboxId}
          ref={listboxRef}
          role="listbox"
          aria-label={fieldLabel ? `${t('cinematic.choose', 'Choose')} ${fieldLabel}` : t('checkout.choosePartnerToSupport')}
          tabIndex={-1}
          onKeyDown={handleListboxKeyDown}
          className={listboxClasses}
        >
          {partners.map((partner, index) => {
            const isSelected = String(partner.id) === String(selected?.id || '');
            const isActive = index === activeIndex;

            return (
              <button
                key={partner.id}
                ref={(node) => { optionRefs.current[index] = node; }}
                type="button"
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choosePartner(partner)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition ${isActive ? optionActive : optionHover} ${isSelected ? optionSelected : optionDefault}`}
              >
                <PartnerAvatar partner={partner} className="h-10 w-10" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{partner.name}</span>
                  {partner.code && <span className={`block truncate text-xs ${optionCode}`}>{formatTranslation(t('checkout.partnerCode'), { code: partner.code })}</span>}
                </span>
                {isSelected && <Check className={`h-4 w-4 shrink-0 ${checkColor}`} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
